import React, { useContext, useMemo, useState, useReducer, useCallback, useEffect } from 'react';
import styles from './PatientContent.module.css';
import { getEnvironmentApiUrl } from '../../../services/General';
import { parsePhysiologicalMontioring } from '../../../services/parsePhysiologicalData';
import { getDefaultTimerMiliSeconds } from '../../../services/util';
import useHttp from '../../../hooks/useHttp';
import { elementsReducer } from '../../../reducers/physiological-reducer';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import { Outlet } from 'react-router-dom';
import { PhysiologicalMonitoring } from '../../../interfaces/PhysiologicalMonitoring';
import { PollValues } from '../../../interfaces/Poll';
import { Case, IScoreCircle } from '../../../interfaces/Patient';
import { Parameter } from '../../../interfaces/Profile';
import ScoresContext from '../../../context/ScoresContext';
import usePatientStore from '../../../stores/patient-store';
import { getLastAndSecondLastScore } from '../../../services/Patient';

interface Props {
    caso: Case;
    updateScore: (cv: IScoreCircle) => void;
    refreshPatientSilently: () => Promise<void>;
}

const TIMER = getDefaultTimerMiliSeconds();

const PatientContent: React.FC<Props> = ({ caso, updateScore, refreshPatientSilently }) => {
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const patient = usePatientStore(state => state.patient);

    const { isLoading, error, sendRequest } = useHttp<PhysiologicalMonitoring[]>();
    const { isLoading: isLoadingInquiries, error: errorInquiries, sendRequest: getInquiries } = useHttp<PollValues[]>();
    const { sendRequest: getSheetParametersCV } = useHttp<Parameter[]>();

    const [sheetParameters, setSheetParameters] = useState<Parameter[] | null>(null);
    const [elements, dispatchElements] = useReducer(elementsReducer, []);

    const parsePhysiological = useCallback((data: PhysiologicalMonitoring[], inquiries: PollValues[]) => {
        const parsedDataElements = parsePhysiologicalMontioring(data, inquiries);
        dispatchElements({ type: 'SET_DEFAULT', payload: parsedDataElements });
    }, []);

    const { scores } = useContext(ScoresContext);

    useEffect(() => {
        const scoreCV = scores?.find(sc => sc.id === 1);
        if (!scoreCV) return;

        const computedScore = getLastAndSecondLastScore(elements, scoreCV);
        updateScore(computedScore);
    }, [elements, scores, updateScore]);

    const { id: case_id, follow_ups: followUps } = caso;
    const activeFollowUp = followUps?.find(fu => fu.end_date === null) ?? followUps?.[0];
    const startDateFollowUp = activeFollowUp?.start_date ?? caso.start_date;
    const endDateFollowUp = activeFollowUp?.end_date ?? caso.end_date;

    const caseFilterData = useMemo(() => {
        return [{
            case_id,
            start_date: startDateFollowUp ?? new Date().toISOString(),
            end_date: endDateFollowUp ?? new Date().toISOString()
        }];
    }, [case_id, endDateFollowUp, startDateFollowUp]);

    const getPatientData = useCallback(() => {
        if (sheetParameters === null || case_id == null) {
            return;
        }

        sendRequest({
            url: getEnvironmentApiUrl() + 'physiological_monitoring/all_case_list',
            method: 'POST',
            body: { params: {}, filter: {}, case_filter_data: caseFilterData, usePatientId: false },
        }, (physiologicals) => {
            getInquiries({
                url: getEnvironmentApiUrl() + 'patient/inquiries_answers',
                method: 'POST',
                body: { case_filter_data: caseFilterData },
            }, (inquiriesData) => {
                parsePhysiological(physiologicals, inquiriesData);
                setIsFirstLoad(false);
            });
        });
    }, [caseFilterData, case_id, getInquiries, parsePhysiological, sendRequest, sheetParameters]);

    const patientID = patient?.id;

    useEffect(() => {
        if (case_id == null || patientID == null) {
            return;
        }

        getSheetParametersCV({
            url: getEnvironmentApiUrl() + 'sheet_parameter/CV',
            method: 'POST',
            body: { patient_id: patientID, case_id }
        }, (data) => {
            setSheetParameters(data);
        });
    }, [case_id, getSheetParametersCV, patientID]);

    useEffect(() => {
        if (!sheetParameters) return;

        getPatientData();

        const timer = setInterval(() => {
            getPatientData();
        }, TIMER);

        return () => clearInterval(timer);
    }, [getPatientData, sheetParameters]);

    return (
        <div className={styles.patientContent}>
            <ErrorSpinnerWrapper
                loading={isFirstLoad && (isLoading || isLoadingInquiries)}
                error={error || errorInquiries}
            >
                <Outlet
                    context={{
                        patient,
                        caso,
                        elements,
                        dispatchElements,
                        sheetParameters,
                        refreshPatientSilently
                    }}
                />
            </ErrorSpinnerWrapper>
        </div>
    );
};

export default PatientContent;